<script>
  import { appById } from '../os/apps.js';
  import { activeId, getWindow } from '../os/windows.svelte.js';
  import { session, logoutAdmin } from '../os/session.svelte.js';
  import { runMenuItem, navigateMenu } from './menubar/menu-command-router.js';
  import { getMenuDefs } from './menubar/menu-definitions.js';
  import MenuPopover from './menubar/MenuPopover.svelte';
  import StatusItems from './menubar/StatusItems.svelte';

  const activeApp = $derived.by(() => {
    const id = activeId();
    if (!id) return appById('finder') ?? { id: 'finder', label: 'Finder' };
    const win = getWindow(id);
    return appById(win?.appId) ?? { id: 'finder', label: 'Finder' };
  });

  const menuDefs = $derived(getMenuDefs(activeApp));

  let activeMenu = $state(null);
  let activeItemIndex = $state(0);

  function handleKeydown(e) {
    const result = navigateMenu(e, { activeMenu, menuDefs, activeItemIndex });
    if (!result) return;
    if (result.runItem) runMenuItem(result.runItem, activeApp);
    activeMenu = result.activeMenu;
    activeItemIndex = result.activeItemIndex;
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div
  class="menubar"
  role="menubar"
  aria-label="Menu bar"
  tabindex="0"
  onmouseleave={() => {
    activeMenu = null;
    activeItemIndex = 0;
  }}
>
  <div class="menu-wrap" role="none" onpointerenter={() => activeMenu && (activeMenu = 'apple')}>
    <button
      class="apple"
      class:open={activeMenu === 'apple'}
      aria-label="Apple menu"
      onclick={() => (activeMenu = activeMenu === 'apple' ? null : 'apple')}
    >
      <svg viewBox="0 0 384 512" role="img">
        <path
          fill="currentColor"
          d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C72.7 141.9 32 175.6 32 244.1c0 20.2 3.7 41.1 11.1 62.6 9.9 28.3 45.6 97.7 82.8 96.6 19.4-.5 33.1-13.8 58.4-13.8 24.5 0 37.2 13.8 58.5 13.8 37.6-.5 69.9-63.7 79.4-92.1-50.4-23.8-47.9-69.7-47.9-71.5zM262.7 113.3c20.4-24.7 18.5-47.2 17.9-55.3-17.9 1-38.6 12.2-50.4 26-13 14.9-20.6 33.3-19 52.9 19.4 1.5 37.2-8.5 51.5-23.6z"
        />
      </svg>
    </button>
    {#if activeMenu === 'apple'}
      <MenuPopover
        items={menuDefs.find((m) => m.id === 'apple').items}
        {activeItemIndex}
        {activeApp}
        onclose={() => {
          activeMenu = null;
          activeItemIndex = 0;
        }}
      />
    {/if}
  </div>

  {#each menuDefs.filter((m) => m.id !== 'apple') as menu (menu.id)}
    <div class="menu-wrap" role="none" onpointerenter={() => activeMenu && (activeMenu = menu.id)}>
      <button
        class={menu.strong ? 'app-name' : 'menu'}
        class:open={activeMenu === menu.id}
        onclick={() => (activeMenu = activeMenu === menu.id ? null : menu.id)}
      >
        {menu.label}
      </button>
      {#if activeMenu === menu.id}
        <MenuPopover
          items={menu.items}
          {activeItemIndex}
          {activeApp}
          onclose={() => {
            activeMenu = null;
            activeItemIndex = 0;
          }}
        />
      {/if}
    </div>
  {/each}
  {#if session.role === 'admin'}
    <button class="menu" onclick={logoutAdmin}>Logout</button>
  {/if}

  <span class="spacer"></span>
  <StatusItems />
</div>

<style>
  /* Extruded 3D slab: content height stays 24px (layout constant MENUBAR_H).
     The z-axis thickness is SHOWN via the ::after edge band + shadow stack,
     which paint below the bar without changing its layout height. */
  .menubar {
    position: relative;
    z-index: 9999999;
    display: flex;
    align-items: stretch;
    height: 24px;
    padding: 0 7px;
    font: 13px var(--font);
    color: #f1f1f1;
    background:
      repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.03) 0 1px, rgba(0, 0, 0, 0.1) 1px 2px),
      linear-gradient(to bottom, var(--menubar-top) 0%, var(--menubar-bot) 100%);
    /* hard black outline across the front face */
    border-bottom: 1px solid var(--bevel-outline);
    box-shadow:
      /* bright top bevel highlight */
      inset 0 1px 0 rgba(255, 255, 255, 0.42),
      inset 0 2px 0 rgba(255, 255, 255, 0.12),
      /* inner shading toward the extruded lip */ inset 0 -3px 4px rgba(0, 0, 0, 0.45),
      /* stacked drop shadows reading as the slab's cast depth */ 0 3px 0 rgba(0, 0, 0, 0.35),
      0 5px 6px rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(12px) saturate(1.3);
    /* 3D stage so labels can pop toward the viewer on the z-axis */
    perspective: 480px;
    transform-style: preserve-3d;
  }
  /* Visible extruded edge band (the z-axis thickness of the slab). */
  .menubar::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 4px;
    background: linear-gradient(
      to bottom,
      var(--bevel-dark) 0%,
      #0b0e0e 55%,
      var(--bevel-outline) 100%
    );
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
    pointer-events: none;
    z-index: -1;
  }
  .menubar > * {
    display: flex;
    align-items: center;
  }

  /* Shared "chip" geometry for every interactive label. */
  .menubar .apple,
  .menubar .app-name,
  .menubar .menu {
    appearance: none;
    display: inline-flex;
    align-items: center;
    color: inherit;
    font: inherit;
    background: transparent;
    border: 0;
    cursor: default;
    border-radius: 3px;
    transform: translateY(0) translateZ(0);
    transition:
      transform 0.16s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.12s ease,
      box-shadow 0.12s ease;
  }
  .menubar .apple {
    padding: 0 8px;
    color: #f1f1f1;
  }
  .menubar .apple svg {
    display: block;
    width: 14px;
    height: 17px;
  }
  .menubar .app-name {
    padding: 0 8px;
    font-weight: 700;
  }
  .menubar .menu {
    padding: 0 7px;
  }

  /* Bumping hover: pop toward viewer with a springy overshoot + top bevel. */
  .menubar .apple:hover,
  .menubar .app-name:hover,
  .menubar .menu:hover {
    background: linear-gradient(to bottom, var(--graphite-select-top), var(--graphite-select-bot));
    color: #fff;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.55);
    transform: translateY(-1px) translateZ(6px) scale(1.04);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 0 0 1px rgba(255, 255, 255, 0.14),
      0 2px 3px rgba(0, 0, 0, 0.5);
  }

  /* Pressed-in / open: sink away from viewer, invert the bevel. */
  .menubar .apple:active,
  .menubar .app-name:active,
  .menubar .menu:active,
  .menubar .apple.open,
  .menubar .app-name.open,
  .menubar .menu.open {
    background: linear-gradient(to bottom, var(--graphite-select-bot), var(--graphite-select-top));
    color: #fff;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.55);
    transform: translateY(1px) translateZ(-2px) scale(0.99);
    box-shadow:
      inset 0 2px 3px rgba(0, 0, 0, 0.55),
      inset 0 -1px 0 rgba(255, 255, 255, 0.14);
  }

  .menu-wrap {
    position: relative;
    display: flex;
    align-items: stretch;
  }
  .menubar .spacer {
    flex: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .menubar {
      perspective: none;
    }
    .menubar .apple,
    .menubar .app-name,
    .menubar .menu {
      transition:
        background 0.12s ease,
        box-shadow 0.12s ease;
      transform: none;
    }
    .menubar .apple:hover,
    .menubar .app-name:hover,
    .menubar .menu:hover,
    .menubar .apple:active,
    .menubar .app-name:active,
    .menubar .menu:active,
    .menubar .apple.open,
    .menubar .app-name.open,
    .menubar .menu.open {
      transform: none;
    }
  }
</style>
